import { useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Pencil, Plus, X, Check } from "lucide-react";
import { ColorBlock, BigStat } from "@/components/page";
import { useResourceList, useResourceDelete, useResourceCreate, useResourceUpdate } from "@/components/data";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

type Staff = { id: number; name: string; role: string };
type Group = { id: number; name: string; day: string; time: string; teacher: string; enrolled: number; capacity: number; };
type Student = { id: number; group_id: number; name: string; phone: string | null; parent_name: string | null; age: string | null; notes: string | null; status: 'פעיל' | 'בהמתנה'; };

const inputCls = "w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300";

function StudentCard({ s, onEdit, onDelete, onToggleStatus, confirmDeleteId, setConfirmDeleteId, updateStudent, deleteStudent }: {
  s: Student;
  onEdit: (s: Student) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (s: Student) => void;
  confirmDeleteId: number | null;
  setConfirmDeleteId: (id: number | null) => void;
  updateStudent: { isPending: boolean };
  deleteStudent: { isPending: boolean };
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Student>>({});

  function startEdit() {
    setIsEditing(true);
    setEditForm({ name: s.name, phone: s.phone ?? "", parent_name: s.parent_name ?? "", age: s.age ?? "" });
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white px-4 py-3 space-y-2">
      {isEditing ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder="שם *" value={String(editForm.name ?? "")}
              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            <input className={inputCls} placeholder="גיל" value={String(editForm.age ?? "")}
              onChange={e => setEditForm(f => ({ ...f, age: e.target.value }))} />
            <input className={inputCls} placeholder="טלפון הורה" value={String(editForm.phone ?? "")}
              onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            <input className={inputCls} placeholder="שם הורה" value={String(editForm.parent_name ?? "")}
              onChange={e => setEditForm(f => ({ ...f, parent_name: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { onEdit({ ...s, ...editForm } as Student); setIsEditing(false); }}
              disabled={updateStudent.isPending}
              className="flex items-center gap-1 rounded-lg bg-stone-900 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
              <Check className="h-3 w-3" /> {updateStudent.isPending ? "שומר…" : "שמור"}
            </button>
            <button type="button" onClick={() => setIsEditing(false)}
              className="text-xs text-stone-400 hover:text-stone-600">ביטול</button>
          </div>
        </>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="font-medium text-stone-900 text-sm">{s.name}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-stone-500">
              {s.age && <span>גיל {s.age}</span>}
              {s.phone && <span dir="ltr">{s.phone}</span>}
              {s.parent_name && <span>הורה: {s.parent_name}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {confirmDeleteId === s.id ? (
              <>
                <button type="button" onClick={() => onDelete(s.id)} disabled={deleteStudent.isPending}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50">
                  {deleteStudent.isPending ? "מוחק…" : "מחק"}
                </button>
                <button type="button" onClick={() => setConfirmDeleteId(null)}
                  className="text-xs text-stone-400 hover:text-stone-600">ביטול</button>
              </>
            ) : (
              <>
                {/* Status badge — click to toggle */}
                <button
                  type="button"
                  onClick={() => onToggleStatus(s)}
                  title={s.status === 'פעיל' ? 'לחץ להעברה לבהמתנה' : 'לחץ להעברה לפעיל'}
                  className={
                    s.status === 'פעיל'
                      ? "rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700 hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer"
                      : "rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 hover:bg-emerald-100 hover:text-emerald-700 transition-colors cursor-pointer"
                  }
                >
                  {s.status}
                </button>
                <button type="button" onClick={startEdit}
                  className="text-stone-400 hover:text-stone-700 transition-colors">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => setConfirmDeleteId(s.id)}
                  className="text-stone-400 hover:text-rose-500 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StudentSection({ label, students, dotCls, ...cardProps }: {
  label: string; students: Student[]; dotCls: string;
  onEdit: (s: Student) => void; onDelete: (id: number) => void; onToggleStatus: (s: Student) => void;
  confirmDeleteId: number | null; setConfirmDeleteId: (id: number | null) => void;
  updateStudent: { isPending: boolean }; deleteStudent: { isPending: boolean };
}) {
  if (students.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{label} ({students.length})</p>
      </div>
      {students.map(s => <StudentCard key={s.id} s={s} {...cardProps} />)}
    </div>
  );
}

function GroupDetail({ group, onClose }: { group: Group; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const queryKey = ["paamon_students", "list", group.id];

  const { data: students, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<Student[]> => {
      const res = await api(`/api/paamon_students?group_id=${group.id}`);
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json() as Promise<Student[]>;
    },
  });

  const createStudent = useResourceCreate<Student>("paamon_students");
  const updateStudent = useResourceUpdate("paamon_students");
  const deleteStudent = useResourceDelete("paamon_students");
  const updateGroup = useResourceUpdate("paamon_groups");

  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", phone: "", parent_name: "", age: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [editingGroup, setEditingGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: group.name, day: group.day, time: group.time, teacher: group.teacher, capacity: String(group.capacity) });

  function invalidate() {
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["paamon_groups", "list"] });
  }

  function handleEdit(s: Student) {
    updateStudent.mutate(
      { id: s.id, data: { name: s.name, phone: s.phone, parent_name: s.parent_name, age: s.age } as Record<string, unknown> },
      { onSuccess: () => invalidate() },
    );
  }

  function handleToggleStatus(s: Student) {
    updateStudent.mutate(
      { id: s.id, data: { status: s.status === 'פעיל' ? 'בהמתנה' : 'פעיל' } },
      { onSuccess: () => invalidate() },
    );
  }

  function handleDelete(id: number) {
    deleteStudent.mutate(id, { onSuccess: () => { setConfirmDeleteId(null); invalidate(); } });
  }

  function handleAddSubmit() {
    if (!addForm.name.trim()) return;
    createStudent.mutate(
      { group_id: group.id, name: addForm.name, phone: addForm.phone || null, parent_name: addForm.parent_name || null, age: addForm.age || null },
      { onSuccess: () => { setAdding(false); setAddForm({ name: "", phone: "", parent_name: "", age: "" }); invalidate(); } },
    );
  }

  function handleGroupSave() {
    updateGroup.mutate(
      { id: group.id, data: { ...groupForm, capacity: parseInt(groupForm.capacity, 10) || group.capacity } },
      { onSuccess: () => { setEditingGroup(false); qc.invalidateQueries({ queryKey: ["paamon_groups", "list"] }); } },
    );
  }

  const active = students?.filter(s => s.status === 'פעיל') ?? [];
  const pending = students?.filter(s => s.status === 'בהמתנה') ?? [];
  const sharedCardProps = { confirmDeleteId, setConfirmDeleteId, updateStudent, deleteStudent, onEdit: handleEdit, onDelete: handleDelete, onToggleStatus: handleToggleStatus };

  return (
    <div className="border-t border-stone-100 bg-stone-50 px-6 py-5 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">פרטי קבוצה</p>
        <div className="flex items-center gap-2">
          {editingGroup ? (
            <>
              <button type="button" onClick={handleGroupSave} disabled={updateGroup.isPending}
                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50">שמור</button>
              <button type="button" onClick={() => setEditingGroup(false)}
                className="text-xs text-stone-400 hover:text-stone-600">ביטול</button>
            </>
          ) : (
            <button type="button" onClick={() => setEditingGroup(true)}
              className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-700 transition-colors">
              <Pencil className="h-3 w-3" /> ערוך קבוצה
            </button>
          )}
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {editingGroup && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {([
            ["שם", "name"], ["יום", "day"], ["שעה", "time"], ["מורה", "teacher"], ["קיבולת", "capacity"],
          ] as [string, keyof typeof groupForm][]).map(([label, field]) => (
            <label key={field} className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">{label}</span>
              <input className={inputCls} value={groupForm[field]}
                onChange={e => setGroupForm(f => ({ ...f, [field]: e.target.value }))} />
            </label>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
            {isLoading ? "טוען…" : `${students?.length ?? 0} ילדים`}
          </p>
          <button type="button" onClick={() => setAdding(v => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-stone-900 transition-colors">
            <Plus className="h-3.5 w-3.5" /> הוסף ילד
          </button>
        </div>

        {adding && (
          <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
            <p className="text-xs font-semibold text-stone-700">ילד חדש</p>
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} placeholder="שם *" value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
              <input className={inputCls} placeholder="גיל" value={addForm.age}
                onChange={e => setAddForm(f => ({ ...f, age: e.target.value }))} />
              <input className={inputCls} placeholder="טלפון הורה" value={addForm.phone}
                onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
              <input className={inputCls} placeholder="שם הורה" value={addForm.parent_name}
                onChange={e => setAddForm(f => ({ ...f, parent_name: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleAddSubmit} disabled={createStudent.isPending || !addForm.name.trim()}
                className="rounded-lg bg-stone-900 px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:bg-stone-700 transition-colors">
                {createStudent.isPending ? "שומר…" : "הוסף"}
              </button>
              <button type="button" onClick={() => setAdding(false)}
                className="text-xs text-stone-400 hover:text-stone-600">ביטול</button>
            </div>
          </div>
        )}

        {students?.length === 0 && !adding && (
          <p className="text-sm text-stone-400 py-2">אין ילדים בקבוצה זו עדיין.</p>
        )}

        <StudentSection label="פעיל" students={active} dotCls="bg-emerald-400" {...sharedCardProps} />
        <StudentSection label="בהמתנה" students={pending} dotCls="bg-amber-400" {...sharedCardProps} />
      </div>
    </div>
  );
}

export function PaamonDashboardPage() {
  const { data: staff, isLoading: sLoading } = useResourceList<Staff>("paamon_staff");
  const { data: groups, isLoading: gLoading } = useResourceList<Group>("paamon_groups");
  const deleteGroup = useResourceDelete("paamon_groups");
  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState<number | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);

  const enrolled = groups?.reduce((s, g) => s + g.enrolled, 0) ?? 0;
  const capacity = groups?.reduce((s, g) => s + g.capacity, 0) ?? 0;
  const occupancy = capacity > 0 ? Math.round((enrolled / capacity) * 100) : 0;
  const tone = (ratio: number) => ratio >= 0.7 ? "bg-emerald-400" : ratio >= 0.4 ? "bg-amber-400" : "bg-rose-400";

  function handleDeleteGroup(id: number) {
    deleteGroup.mutate(id, { onSuccess: () => setConfirmDeleteGroupId(null) });
  }

  return (
    <div className="space-y-6 sm:space-y-7">
      <header className="pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">לוח בקרה — פעמון</p>
        <h1 className="mt-3 flex items-baseline gap-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
          <span className="tabular-nums" dir="ltr">{gLoading ? "…" : `${enrolled}/${capacity}`}</span>
          <span className="text-stone-300 text-lg sm:text-xl">·</span>
          <span className="text-stone-700 text-lg sm:text-xl">תפוסה</span>
        </h1>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
          {gLoading ? "טוען…" : `${groups?.length ?? 0} קבוצות פעילות, ${occupancy}% תפוסה. יעד: 75%.`}
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <ColorBlock tint="sage" size="sm"><BigStat label="קבוצות" value={gLoading ? "…" : groups?.length ?? 0} size="md" /></ColorBlock>
        <ColorBlock tint="blue" size="sm"><BigStat label="צוות" value={sLoading ? "…" : staff?.length ?? 0} size="md" /></ColorBlock>
        <ColorBlock tint="peach" size="sm"><BigStat label="תפוסה" value={`${occupancy}%`} size="md" /></ColorBlock>
        <ColorBlock tint="cream" size="sm"><BigStat label="חסרים" value={gLoading ? "…" : Math.max(0, capacity - enrolled)} size="md" /></ColorBlock>
      </div>

      <div className="rounded-[24px] bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-200/70">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">צוות המרכז</p>
          <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">{staff?.length ?? 0} חברי צוות</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">שם</th>
            <th className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">תפקיד</th>
          </tr></thead>
          <tbody>
            {staff?.map(s => (
              <tr key={s.id} className="border-t border-stone-200/70 hover:bg-stone-50 transition-colors">
                <td className="px-6 py-4 font-medium text-stone-900">{s.name}</td>
                <td className="px-6 py-4 text-stone-600">{s.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-[24px] bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-200/70">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">קבוצות</p>
          <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">{groups?.length ?? 0} קבוצות פעילות</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="w-8 px-4 py-3" />
            <th className="text-start px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">קבוצה</th>
            <th className="text-start px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">יום</th>
            <th className="text-start px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">שעה</th>
            <th className="text-start px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">מורה</th>
            <th className="text-start px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">תפוסה</th>
            <th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {groups?.map(g => {
              const ratio = g.capacity > 0 ? g.enrolled / g.capacity : 0;
              const isExpanded = expandedGroupId === g.id;
              const isConfirming = confirmDeleteGroupId === g.id;
              const isDeleting = deleteGroup.isPending && confirmDeleteGroupId === g.id;
              return (
                <>
                  <tr key={g.id} className={`border-t border-stone-200/70 transition-colors ${isExpanded ? "bg-stone-50" : "hover:bg-stone-50"}`}>
                    <td className="px-4 py-4">
                      <button type="button" onClick={() => setExpandedGroupId(isExpanded ? null : g.id)}
                        className="text-stone-400 hover:text-stone-700 transition-colors">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-4 font-medium text-stone-900">
                      <button type="button" onClick={() => setExpandedGroupId(isExpanded ? null : g.id)}
                        className="hover:text-stone-600 transition-colors text-start">{g.name}</button>
                    </td>
                    <td className="px-4 py-4 text-stone-600">{g.day}</td>
                    <td className="px-4 py-4 text-stone-600 tabular-nums" dir="ltr">{g.time}</td>
                    <td className="px-4 py-4 text-stone-600">{g.teacher}</td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full ${tone(ratio)}`} />
                        <span className="tabular-nums text-stone-700" dir="ltr">{g.enrolled}/{g.capacity}</span>
                      </span>
                    </td>
                    <td className="px-4 py-4 text-end">
                      {isConfirming ? (
                        <span className="inline-flex items-center gap-2">
                          <button type="button" onClick={() => handleDeleteGroup(g.id)} disabled={isDeleting}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50">
                            {isDeleting ? "מוחק…" : "מחק"}
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteGroupId(null)}
                            className="text-xs text-stone-400 hover:text-stone-600">ביטול</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => setConfirmDeleteGroupId(g.id)}
                          className="text-stone-400 hover:text-rose-500 transition-colors" aria-label={`מחק ${g.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${g.id}-detail`}>
                      <td colSpan={7} className="p-0">
                        <GroupDetail group={g} onClose={() => setExpandedGroupId(null)} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
